import { history, RunTimeLayoutConfig } from '@umijs/max';
import { ADMIN_TOKEN_KEY, WRITE_TOKEN } from './constants';
import { renderRightSlot } from './right-slot';

/**
 * umi request 运行时配置（@umijs/max 约定导出）。
 *
 * v2 登录体系：Authorization 头优先使用管理员会话令牌（localStorage ADMIN_TOKEN_KEY），
 * 未登录时回落到内置 WRITE_TOKEN 作为部署期兜底；二者都由后端双轨校验。
 * 注意：本文件保持零 JSX——umi appData 阶段的 es-module-lexer 不支持 JSX，
 * 所有界面元素一律放在独立 .tsx 模块中。
 */
export const request = {
  requestInterceptors: [
    (url: string, options: any) => {
      const adminToken =
        typeof localStorage !== 'undefined' ? localStorage.getItem(ADMIN_TOKEN_KEY) || '' : '';
      const bearer = adminToken || WRITE_TOKEN;
      if (bearer) {
        return {
          url,
          options: {
            ...options,
            headers: {
              ...(options?.headers || {}),
              Authorization: `Bearer ${bearer}`,
            },
          },
        };
      }
      return { url, options };
    },
    [
      (response: any) => response,
      (error: any) => {
        try {
          const status = error?.response?.status;
          const path = window.location.pathname;
          if (status === 401 && path !== '/login' && !path.startsWith('/s/')) {
            localStorage.removeItem(ADMIN_TOKEN_KEY);
            history.push('/login');
          }
        } catch {
          /* 忽略拦截器内异常 */
        }
        throw error;
      },
    ],
  ],
};

/** 初始状态：启动时用本地令牌验证一次身份 */
export async function getInitialState(): Promise<{
  user: { id?: number; username?: string } | null;
}> {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(ADMIN_TOKEN_KEY) || '' : '';
  if (!token) return { user: null };
  try {
    const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(String(res.status));
    const user = await res.json();
    return { user };
  } catch {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    return { user: null };
  }
}

function logout() {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(ADMIN_TOKEN_KEY);
  history.push('/login');
}

/** 布局运行时：页面守卫 + 右上角身份与退出 */
export const layout: RunTimeLayoutConfig = ({ initialState }) => ({
  rightRender: () => renderRightSlot((initialState as any)?.user ?? null, logout),
  onPageChange: () => {
    const loc = (history as any).location;
    if (loc.pathname === '/login' || String(loc.pathname).startsWith('/s/')) return;
    // 权威信号是 localStorage 里的令牌而非内存态：
    // 避免“登录成功 → history.push('/dashboard') → 守卫读到旧的空 user → 弹回 /login”竞态
    const hasToken = typeof localStorage !== 'undefined' && !!localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!hasToken) {
      history.push('/login');
    }
  },
});
